import type { Storey } from '@shared/models/design/storey.model';
import type { Point2D } from '@shared/models/geometry.model';
import { getStoreyElevationRange } from '@shared/helpers/design/storey.helpers';

export const FACADE_DIMENSION_LABEL_LINE_HEIGHT_PX = 18;
const CONTOUR_GAP_PX = 12;

export type FacadeBuildingDimensions = {
  points: Point2D[];
  height: number;
  depth: number;
};
export type FacadeDimensionLabel = {
  kind: 'height' | 'depth';
  meters: number;
  screenPoint: Point2D;
};

/** Full building extents relative to ground zero, excluding storeys without a solid volume. */
export const getFacadeBuildingDimensions = (
  storeys: Storey[],
): FacadeBuildingDimensions => {
  let top = 0;
  let bottom = 0;
  const points: Point2D[] = [];
  for (const storey of storeys) {
    const solids = storey.shapes.filter(
      (shape) => !shape.cutout && shape.points.length >= 3,
    );
    if (!solids.length) continue;
    const range = getStoreyElevationRange(storeys, storey);
    top = Math.max(top, range.top);
    bottom = Math.min(bottom, range.bottom);
    points.push(...solids.flatMap((shape) => shape.points));
  }
  return { points, height: top, depth: -bottom };
};

/** Place text right of the complete contour with one text-row of clear space to the horizon. */
export const getFacadeDimensionLabels = (input: {
  dimensions: FacadeBuildingDimensions;
  project: (point: Point2D, elevation: number) => Point2D | undefined;
}): FacadeDimensionLabel[] => {
  const anchor = input.dimensions.points[0];
  if (!anchor) return [];
  const ground = input.project(anchor, 0);
  if (!ground) return [];
  const projected = input.dimensions.points.map((point) =>
    input.project(point, 0),
  );
  const right = projected.reduce(
    (maximum, point) => (point ? Math.max(maximum, point[0]) : maximum),
    -Infinity,
  );
  if (!Number.isFinite(right)) return [];
  const clearance = FACADE_DIMENSION_LABEL_LINE_HEIGHT_PX * 1.5;
  const labels: FacadeDimensionLabel[] = [];
  const { height, depth } = input.dimensions;
  if (height > 0) {
    const middle = input.project(anchor, height / 2);
    if (middle)
      labels.push({
        kind: 'height',
        meters: height,
        screenPoint: [
          right + CONTOUR_GAP_PX,
          Math.min(middle[1], ground[1] - clearance),
        ],
      });
  }
  if (depth > 0) {
    const middle = input.project(anchor, -depth / 2);
    if (middle)
      labels.push({
        kind: 'depth',
        meters: depth,
        screenPoint: [
          right + CONTOUR_GAP_PX,
          Math.max(middle[1], ground[1] + clearance),
        ],
      });
  }
  return labels;
};
